import { useMutation } from '@tanstack/react-query';
import { Input, Text, Flex, chakra, IconButton, Icon } from '@chakra-ui/react';
import { Controller, FormProvider, Resolver, SubmitHandler, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { api, BranchDto, CreateBranchDto, UpdateBranchDto } from '../../lib/api';
import { accreditationBodies } from '../../utils/coding-details';
import { useNavigate, useParams, useRouteLoaderData } from 'react-router-dom';
import Address from '../../components/address';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { CirclePlus, DollarSign, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toaster } from '@/components/ui/toaster';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PhoneInput } from '@/components/form/phone-input';
import { FaxInput } from '@/components/form/fax-input';
import { Page } from '@/components/layout/page';
import { InputGroup } from '@/components/ui/input-group';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { addressValidationSchema, phoneSchema, faxOptionalSchema } from '@musa/lib';

const Hours = ({
    day,
    model,
}: {
    day: string;
    model:
        | 'operatingHours.monday'
        | 'operatingHours.tuesday'
        | 'operatingHours.wednesday'
        | 'operatingHours.thursday'
        | 'operatingHours.friday'
        | 'operatingHours.saturday'
        | 'operatingHours.sunday';
}) => {
    const {
        register,
        getValues,
        setValue,
        control,
        trigger,
        formState: { errors },
    } = useFormContext<UpdateBranchDto>();
    const modelValues = getValues(model) || [];
    const isChecked = modelValues.length > 0 ? true : false;
    const { fields, append, remove } = useFieldArray({
        control,
        name: model,
    });

    // re render on change
    useWatch<UpdateBranchDto>({
        control,
        name: model,
    });

    return (
        <Flex flexDirection="column" border="solid" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p="8px" mt="2">
            <Flex alignItems="center">
                <Switch
                    size="md"
                    checked={isChecked}
                    onCheckedChange={({ checked }) => {
                        if (checked) {
                            setValue(model, [{ start: '08:00', end: '17:00' }]);
                        } else {
                            setValue(model, []);
                        }
                    }}
                />
                <Text pl="3" textTransform="capitalize">
                    {day}
                </Text>
            </Flex>
            <Flex gap="12px" flexDirection="column">
                {fields.map((field, index) => {
                    const [, day] = model.split('.') as [
                        string,
                        'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
                    ];
                    const { onBlur: onBlurStart, ...startProps } = register(`${model}.${index}.start`);
                    const { onBlur: onBlurEnd, ...endProps } = register(`${model}.${index}.end`);
                    return (
                        <Flex key={field.id} gap="8px" mt={{ _first: '8px' }}>
                            <Flex gap="8px">
                                <Field
                                    invalid={!!errors.operatingHours?.[day]?.[index]?.start}
                                    errorText={errors.operatingHours?.[day]?.[index]?.start?.message}>
                                    <Input
                                        type="time"
                                        {...startProps}
                                        onBlur={(e) => {
                                            trigger(`${model}.${index}.end`);
                                            onBlurStart(e);
                                        }}
                                    />
                                </Field>
                                <Flex h="36px" align="center">
                                    <Text>to</Text>
                                </Flex>
                                <Field
                                    invalid={!!errors.operatingHours?.[day]?.[index]?.end}
                                    errorText={errors.operatingHours?.[day]?.[index]?.end?.message}>
                                    <Input
                                        type="time"
                                        {...endProps}
                                        onBlur={(e) => {
                                            trigger(`${model}.${index}.start`);
                                            onBlurEnd(e);
                                        }}
                                    />
                                </Field>
                                <Flex alignItems="center" gap="8px" h="36px">
                                    {index === modelValues.length - 1 ? (
                                        <IconButton
                                            h="20px"
                                            minW="20px"
                                            aria-label="Options"
                                            rounded="2xl"
                                            disabled={!getValues(`${model}.${index}.start`) || !getValues(`${model}.${index}.end`)}
                                            onClick={() => {
                                                append({ start: '', end: '' });
                                            }}
                                            variant="plain">
                                            <CirclePlus />
                                        </IconButton>
                                    ) : (
                                        <IconButton visibility="hidden" h="20px" minW="20px" rounded="2xl"></IconButton>
                                    )}
                                    <IconButton
                                        h="20px"
                                        minW="20px"
                                        aria-label="Options"
                                        rounded="2xl"
                                        colorPalette="red"
                                        onClick={() => {
                                            remove(index);
                                        }}
                                        variant="ghost">
                                        <Trash2 />
                                    </IconButton>
                                </Flex>
                            </Flex>
                        </Flex>
                    );
                })}
            </Flex>
        </Flex>
    );
};

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

// form values and validations
const defaultHours = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
};

const defaultValues: UpdateBranchDto = {
    name: '',
    medicareProviderNumber: '',
    administrator: '',
    administratorPhone: '',
    administratorFax: null,
    statefacilityId: '',
    stateBranchId: '',
    npi: '',
    ptan: '',
    accreditationBody: '',
    accreditationId: '',
    operatingHours: defaultHours,
    address: {
        address: '',
        city: '',
        state: '',
        zipcode: '',
    },
    hotlineName: '',
    hotlinePhone: '',
    hotlineAddress: {
        address: '',
        city: '',
        state: '',
        zipcode: '',
    },
    defaultChargeSN: '',
    defaultChargePT: '',
    defaultChargeOT: '',
    defaultChargeST: '',
    defaultChargeHHA: '',
    defaultChargeMSW: '',
};

const hhmmToMinutes = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const operatingHours = z
    .object({
        start: z.string().regex(/^\d{2}:[0-5]\d$/, 'Start must be in HH:mm'),
        end: z.string().regex(/^\d{2}:[0-5]\d$/, 'End must be in HH:mm'),
    })
    .refine(
        ({ start, end }) => {
            return hhmmToMinutes(end) >= hhmmToMinutes(start);
        },
        {
            message: 'End time must be at or after start time',
            path: ['end'],
        },
    )
    .refine(
        ({ start, end }) => {
            return hhmmToMinutes(end) >= hhmmToMinutes(start);
        },
        {
            message: 'End time must be at or after start time',
            path: ['start'],
        },
    );

const weeklyOperatingHoursSchema = z.object({
    monday: z.array(operatingHours).optional(),
    tuesday: z.array(operatingHours).optional(),
    wednesday: z.array(operatingHours).optional(),
    thursday: z.array(operatingHours).optional(),
    friday: z.array(operatingHours).optional(),
    saturday: z.array(operatingHours).optional(),
    sunday: z.array(operatingHours).optional(),
});

const validationSchema: Resolver<UpdateBranchDto> = zodResolver(
    z.object({
        name: z.string().min(1, 'Name required'),
        address: addressValidationSchema,
        ptan: z.string().min(1, 'PTAN is required'),
        npi: z.string().min(1, 'NPI is required'),
        stateBranchId: z.string().optional(),
        statefacilityId: z.string().optional(),
        accreditationBody: z.string().optional(),
        accreditationId: z.string().optional(),
        hotlineName: z.string().min(1, 'Hotline name is required'),
        hotlineAddress: addressValidationSchema,
        hotlinePhone: phoneSchema,
        operatingHours: weeklyOperatingHoursSchema,
        administrator: z.string().min(1, 'required'),
        administratorPhone: phoneSchema,
        administratorFax: faxOptionalSchema,
        defaultChargeSN: z.string().min(1, 'required'),
        defaultChargePT: z.string().min(1, 'required'),
        defaultChargeOT: z.string().min(1, 'required'),
        defaultChargeST: z.string().min(1, 'required'),
        defaultChargeHHA: z.string().min(1, 'required'),
        defaultChargeMSW: z.string().min(1, 'required'),
    }),
);

export function BranchPage() {
    const data = useRouteLoaderData('branch-details') as BranchDto;
    const { companyId, branchId } = useParams<'branchId' | 'companyId'>();
    const navigate = useNavigate();
    const { mutate, isPending } = useMutation<unknown, { error: Error }, CreateBranchDto | UpdateBranchDto>({
        mutationFn: async (data) => {
            if (companyId) {
                if (branchId) {
                    return api.branch.update(branchId, companyId, data as UpdateBranchDto);
                }
                return api.branch.create(companyId, data as CreateBranchDto);
            }
        },
        onSuccess: () => {
            navigate('/organization/overview');
        },
        onError: (error) => {
            toaster.create({
                title: `Unable to create branch: ${error.error.message}`,
                type: 'error',
                duration: 3000,
            });
        },
    });

    // handlers
    const onSubmit: SubmitHandler<UpdateBranchDto> = async (data) => {
        mutate(data);
    };

    const rhf = useForm<UpdateBranchDto>({
        values: { ...defaultValues, ...data },
        resolver: validationSchema,
        reValidateMode: 'onChange',
        mode: 'onChange',
    });

    const {
        formState: { errors },
        register,
    } = rhf;

    return (
        <Page>
            <Flex gap="8" flexDirection="column">
                <FormProvider {...rhf}>
                    <chakra.form noValidate onSubmit={rhf.handleSubmit(onSubmit)}>
                        <Flex flexDirection="column" gap="6">
                            <Card type="standard" barTitle="Branch Details">
                                <Field required invalid={!!errors.name} label="Branch name" errorText={errors.name?.message}>
                                    <Input type="text" {...register('name')} id="name" name="name" />
                                </Field>
                                <Address
                                    isRequired={true}
                                    validAddress={!!errors?.address?.address}
                                    validCity={!!errors?.address?.city}
                                    validState={!!errors?.address?.state}
                                    validZipCode={!!errors?.address?.zipcode}
                                    errorAddress={errors?.address?.address?.message}
                                    errorCity={errors?.address?.city?.message}
                                    errorState={errors?.address?.state?.message}
                                    errorZipCode={errors?.address?.zipcode?.message}
                                    nameAddress="address.address"
                                    nameCity="address.city"
                                    nameState="address.state"
                                    nameZipCode="address.zipcode"
                                />
                            </Card>
                            <Card type="standard" barTitle="Administrator">
                                <FieldRow>
                                    <Field label="Name" required invalid={!!errors.administrator} errorText={errors.administrator?.message}>
                                        <Input type="text" {...register('administrator')} />
                                    </Field>
                                    <Field
                                        label="Phone"
                                        required
                                        invalid={!!errors.administratorPhone}
                                        errorText={errors.administratorPhone?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="administratorPhone"
                                            render={({ field }) => (
                                                <PhoneInput iconSize={16} {...field} onChange={(e) => field.onChange(e.target.value)} />
                                            )}
                                        />
                                    </Field>
                                    <Field label="Fax" invalid={!!errors.administratorFax} errorText={errors.administratorFax?.message}>
                                        <Controller
                                            control={rhf.control}
                                            name="administratorFax"
                                            render={({ field: { value, onChange, ...props } }) => (
                                                <FaxInput {...props} value={value || ''} onChange={(e) => onChange(e.target.value)} />
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                            </Card>
                            <Card type="standard" barTitle="Licenses">
                                <FieldRow>
                                    <Field label="NPI" invalid={!!errors.npi} errorText={errors.npi?.message}>
                                        <Input type="text" {...register('npi')} maxLength={10} id="npi" name="npi" />
                                    </Field>

                                    <Field label="PTAN" invalid={!!errors.ptan} errorText={errors.ptan?.message}>
                                        <Input type="text" {...register('ptan')} id="ptan" name="ptan" maxLength={6} />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="State Agency ID"
                                        invalid={!!errors.statefacilityId}
                                        errorText={errors.statefacilityId?.message}>
                                        <Input
                                            type="text"
                                            {...register('statefacilityId')}
                                            id="statefacilityId"
                                            name="statefacilityId"
                                            data-testid="input-statefacilityId"
                                        />
                                    </Field>
                                    <Field
                                        label="State Branch ID"
                                        invalid={!!errors.stateBranchId}
                                        errorText={errors.stateBranchId?.message}>
                                        <Input type="text" {...register('stateBranchId')} id="stateBranchId" name="stateBranchId" />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Accreditation Body"
                                        invalid={!!errors.accreditationBody}
                                        errorText={errors.accreditationBody?.message}>
                                        <NativeSelectRoot>
                                            <NativeSelectField {...register('accreditationBody')}>
                                                {accreditationBodies.map((body) => {
                                                    return (
                                                        <option key={body} value={body}>
                                                            {body === '' ? 'Select an accreditation body' : body}
                                                        </option>
                                                    );
                                                })}
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>
                                    <Field
                                        label="Accreditation ID"
                                        invalid={!!errors.accreditationId}
                                        errorText={errors.accreditationId?.message}>
                                        <Input type="text" {...register('accreditationId')} id="accreditationId" name="accreditationId" />
                                    </Field>
                                </FieldRow>
                            </Card>
                            <Card type="standard" barTitle="Operating Hours">
                                <FieldGroup>
                                    {days.map((day) => (
                                        <Hours key={day} day={day} model={`operatingHours.${day}`} />
                                    ))}
                                </FieldGroup>
                            </Card>
                            <Card type="standard" barTitle="Default Charges">
                                <FieldGroup>
                                    <FieldRow>
                                        <Field label="SN" invalid={!!errors.defaultChargeSN} errorText={errors.defaultChargeSN?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargeSN"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                        <Field label="PT" invalid={!!errors.defaultChargePT} errorText={errors.defaultChargePT?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargePT"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                    </FieldRow>
                                    <FieldRow>
                                        <Field label="OT" invalid={!!errors.defaultChargeOT} errorText={errors.defaultChargeOT?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargeOT"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                        <Field label="ST" invalid={!!errors.defaultChargeST} errorText={errors.defaultChargeST?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargeST"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                    </FieldRow>
                                    <FieldRow>
                                        <Field label="HHA" invalid={!!errors.defaultChargeHHA} errorText={errors.defaultChargeHHA?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargeHHA"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                        <Field label="MSW" invalid={!!errors.defaultChargeMSW} errorText={errors.defaultChargeMSW?.message}>
                                            <Controller
                                                control={rhf.control}
                                                name="defaultChargeMSW"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value}
                                                        onValueChange={({ value }) => field.onChange(value)}
                                                        width="100%">
                                                        <InputGroup
                                                            width="100%"
                                                            startElement={
                                                                <Icon size="xs">
                                                                    <DollarSign />
                                                                </Icon>
                                                            }>
                                                            <NumberInputField bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                                        </InputGroup>
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                    </FieldRow>
                                </FieldGroup>
                            </Card>
                            <Card type="standard" barTitle="Hotline">
                                <Field
                                    label="State Hotline Number"
                                    required
                                    invalid={!!errors.hotlineName?.message}
                                    errorText={errors.hotlineName?.message}>
                                    <Input type="text" {...register('hotlineName')} id="hotlineName" name="hotlineName" />
                                </Field>
                                <Address
                                    isRequired={true}
                                    validAddress={!!errors?.hotlineAddress?.address}
                                    validCity={!!errors?.hotlineAddress?.city}
                                    validState={!!errors?.hotlineAddress?.state}
                                    validZipCode={!!errors?.hotlineAddress?.zipcode}
                                    errorAddress={errors?.hotlineAddress?.address?.message}
                                    errorCity={errors?.hotlineAddress?.city?.message}
                                    errorState={errors?.hotlineAddress?.state?.message}
                                    errorZipCode={errors?.hotlineAddress?.zipcode?.message}
                                    nameAddress="hotlineAddress.address"
                                    nameCity="hotlineAddress.city"
                                    nameState="hotlineAddress.state"
                                    nameZipCode="hotlineAddress.zipcode"
                                />
                                <Field
                                    marginTop="4"
                                    label="Phone"
                                    required
                                    errorText={errors?.hotlinePhone?.message}
                                    invalid={!!errors.hotlinePhone}>
                                    <Controller
                                        name="hotlinePhone"
                                        control={rhf.control}
                                        render={({ field }) => (
                                            <PhoneInput iconSize={16} {...field} onChange={(e) => field.onChange(e.target.value)} />
                                        )}
                                    />
                                </Field>
                            </Card>
                            <Flex>
                                <Button
                                    type="submit"
                                    disabled={rhf.formState.isSubmitting}
                                    loadingText="saving"
                                    loading={isPending}
                                    data-testid="button-submit">
                                    {data ? 'Save' : 'Create'}
                                </Button>
                            </Flex>
                        </Flex>
                    </chakra.form>
                </FormProvider>
            </Flex>
        </Page>
    );
}
